import "../lobby.css";
import { useEffect, useState, useRef } from "react";
import { Row, Col, Modal, Button, Spinner } from "react-bootstrap";
import { playSound, handleClickSound } from "../../game/scenes/classes/PlaySound";
import { session, client } from "../../../utils/nakama";
import { useWallet } from "../../../contexts/WalletContext";
import { useConnection } from "@solana/wallet-adapter-react";
import { usePublicClient, useReadContract, useSwitchChain } from 'wagmi';
import { erc20Abi, formatUnits } from 'viem';
import { Transaction, PublicKey } from '@solana/web3.js';
import { createTransferInstruction, getAssociatedTokenAddress } from '@solana/spl-token';
import { API_BASE_URL } from "../../../services/x402Api";
import { wrapFetchWithPayment } from "@x402/fetch";
import { x402Client, x402HTTPClient } from "@x402/core/client";
import { registerExactEvmScheme } from "@x402/evm/exact/client";
import { registerExactSvmScheme } from "@x402/svm/exact/client";
import { WalletConnect } from "../../../components/WalletConnect";
import { base, baseSepolia } from "viem/chains";
import eventsCenter, { PHASER_EVENTS } from "../../../utils/phaserEvents";

import chipz1 from "../../../assets/images/chipz_1.png";
import chipz2 from "../../../assets/images/chipz_2.png";
import chipz3 from "../../../assets/images/chipz_3.png";
import chipz4 from "../../../assets/images/chipz_4.png";
import creditCardIcon from "../../../assets/images/creditcard_icon.png";
import usdcIcon from "../../../assets/images/usd-coin-usdc-logo.png";
import baseIcon from "../../../assets/images/base-lockup-white.svg";
import solanaIcon from "../../../assets/images/solana-logo.svg";

// Network configuration - user's network selection is the source of truth
// NODE_ENV controls dev vs prod (testnets vs mainnets)
// LOCAL flag controls service URLs (localhost vs production)
const NETWORK_CONFIG = {
    base: {
        chainId: 8453, // Base mainnet
        chainIdDev: 84532, // Base Sepolia testnet
        rpc: "https://mainnet.base.org",
        rpcDev: "https://sepolia.base.org",
        usdc: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", // Base mainnet USDC
        usdcDev: "0x036CbD53842c5426634e7929541eC2318f3dCF7e", // Base Sepolia test USDC
        chainName: "Base",
        chainNameDev: "Base Sepolia",
        blockExplorer: "https://basescan.org",
        blockExplorerDev: "https://sepolia.basescan.org",
        type: "evm", // EVM-based network
        viemChain: base, // viem chain object for mainnet
        viemChainDev: baseSepolia // viem chain object for devnet
    },
    solana: {
        rpc: "https://api.mainnet-beta.solana.com",
        rpcDev: "https://api.devnet.solana.com",
        usdc: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", // Solana mainnet USDC mint
        usdcDev: "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU", // Solana devnet USDC mint
        type: "solana", // Solana-based network
        chainName: "Solana",
        chainNameDev: "Solana Devnet"
    }
};

// Helper function to get network config based on user selection and environment
// VITE_NODE_ENV=production forces mainnet (for testing mainnet from localhost)
// Otherwise uses VITE_LOCAL or hostname to determine testnet vs mainnet
function getNetworkConfig(networkName) {
    // If VITE_NODE_ENV=production, use mainnet regardless of location
    const nodeEnv = import.meta.env.VITE_NODE_ENV || import.meta.env.MODE;
    const isLocal = import.meta.env.VITE_LOCAL === 'true' || (typeof window !== 'undefined' && window.location.hostname === 'localhost');
    const isDevelopment = nodeEnv === 'production' ? false : isLocal;
    const network = NETWORK_CONFIG[networkName?.toLowerCase()];

    if (!network) {
        throw new Error(`Unknown network: ${networkName}`);
    }

    if (network.type === 'evm') {
        return {
            ...network,
            chainId: isDevelopment ? network.chainIdDev : network.chainId,
            chainIdHex: isDevelopment
                ? `0x${network.chainIdDev.toString(16)}`
                : `0x${network.chainId.toString(16)}`,
            rpc: isDevelopment ? network.rpcDev : network.rpc,
            usdc: isDevelopment ? network.usdcDev : network.usdc,
            chainName: isDevelopment ? network.chainNameDev : network.chainName,
            blockExplorer: isDevelopment ? network.blockExplorerDev : network.blockExplorer,
            viemChain: isDevelopment ? network.viemChainDev : network.viemChain
        };
    } else {
        // Solana or other non-EVM networks
        return {
            ...network,
            rpc: isDevelopment ? network.rpcDev : network.rpc,
            usdc: isDevelopment ? network.usdcDev : network.usdc,
            chainName: isDevelopment ? network.chainNameDev : network.chainName
        };
    }
}

// Package configuration matching server-side config
const PACKAGE_CONFIG = [
    { price: 0.001, chipz: 100000 },   // Package 0: 100,000 chipz for 0.99 USDC
    { price: 4.99, chipz: 525000 },   // Package 1: 500,000 + 5% bonus = 525,000 chipz
    { price: 9.99, chipz: 1070000 },  // Package 2: 1,000,000 + 7% bonus = 1,070,000 chipz
    { price: 49.99, chipz: 5500000 }  // Package 3: 5,000,000 + 10% bonus = 5,500,000 chipz
];

const gtagConversionMap = [
    {
        value: 0.99,
    },
    {
        value: 4.99,
    },
    {
        value: 9.99,
    },
    {
        value: 49.99,
    },
];

export default function CreateShopModal(props) {
    useEffect(() => {
        // Reset network selection when modal opens/closes
        if (!props.showShopModal) {
            setShowNetworkSelection(false);
            setSelectedPackageIndex(null);
            setSelectedNetwork(null);
        }
    }, [props.showShopModal]);

    const [payType, setPayType] = useState(0); // 0 = choose pay type, 1 = Stripe, 2 = Coinbase checkout, 3 = x402
    const [x402Loading, setX402Loading] = useState(false);
    const [x402Error, setX402Error] = useState(null);
    const [showNetworkSelection, setShowNetworkSelection] = useState(false);
    const [selectedPackageIndex, setSelectedPackageIndex] = useState(null);
    const [selectedNetwork, setSelectedNetwork] = useState(null); // User's network selection (source of truth)
    const [basePaymentPending, setBasePaymentPending] = useState(false);
    const [waitingForNotification, setWaitingForNotification] = useState(false);
    const [wrongNetwork, setWrongNetwork] = useState(null); // Stores network info when user is on wrong network

    // Store x402 payment client in ref to persist across renders
    const paymentClientRef = useRef(null);

    // Get public client for RPC calls (needed for x402 Exact scheme)
    const publicClient = usePublicClient();

    // Get wallet from context
    const {
        walletClient,
        isConnected,
        connect,
        connectors,
        solanaWallet,
        solanaConnected,
        solanaAddress,
        solanaWalletName,
        connectSolanaWallet,
        activeWalletType,
        setActiveWalletType
    } = useWallet();

    // Get Solana connection (needed for x402 SVM payments)
    const { connection } = useConnection();

    // Get switchChain function for network switching
    const { switchChain } = useSwitchChain();

    // Check USDC balance for EVM wallets
    // VITE_NODE_ENV=production forces mainnet (for testing mainnet from localhost)
    // Otherwise uses VITE_LOCAL or hostname to determine testnet vs mainnet
    const nodeEnv = import.meta.env.VITE_NODE_ENV || import.meta.env.MODE;
    const isLocal = import.meta.env.VITE_LOCAL === 'true' || (typeof window !== 'undefined' && window.location.hostname === 'localhost');
    const isDevelopment = nodeEnv === 'production' ? false : isLocal;
    const usdcAddress = isDevelopment
        ? NETWORK_CONFIG.base.usdcDev  // Base Sepolia testnet USDC
        : NETWORK_CONFIG.base.usdc;     // Base mainnet USDC

    const { data: usdcBalance, isLoading: isLoadingBalance } = useReadContract({
        address: usdcAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: walletClient?.account?.address ? [walletClient.account.address] : undefined,
        enabled: isConnected && selectedNetwork === 'base' && !!walletClient?.account?.address,
        watch: true, // Re-check when balance changes
    });

    // Format USDC balance (6 decimals)
    const formattedUsdcBalance = usdcBalance
        ? parseFloat(formatUnits(usdcBalance, 6))
        : 0;

    // Check if user has sufficient balance for selected package
    const hasInsufficientFunds = selectedPackageIndex !== null
        && selectedNetwork === 'base'
        && isConnected
        && !isLoadingBalance
        && formattedUsdcBalance < PACKAGE_CONFIG[selectedPackageIndex].price;

    // Static package display data (name + USD amount). Kept for the package summary row in the UI.
    const urlData = [
        { name: "100,000 Chipz", amount: "0.99" },
        { name: "525,000 Chipz", amount: "4.99" },
        { name: "1,070,000 Chipz", amount: "9.99" },
        { name: "5,500,000 Chipz", amount: "49.99" },
    ]


    useEffect(() => {
        // Add the Stripe script dynamically on component mount
        const script = document.createElement('script');
        script.src = "https://js.stripe.com/v3/pricing-table.js";
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        }
    }, [])

    // Register x402 payment client when wallet connects
    useEffect(() => {
        const registerPaymentClient = async () => {
            // Only register if a wallet is connected
            const hasEvmWallet = isConnected && walletClient?.account?.address;
            const hasSolanaWallet = solanaConnected && solanaWallet?.publicKey;

            if (!hasEvmWallet && !hasSolanaWallet) {
                //console.log('[x402] No wallet connected, skipping client registration');
                paymentClientRef.current = null;
                return;
            }

            //console.log('[x402] Wallet connected, registering payment client...');

            try {
                // Create x402 client
                const paymentClient = new x402Client();

                // Register EVM scheme if EVM wallet is connected
                if (hasEvmWallet) {
                    //console.log('[x402] Registering EVM scheme...');

                    // Create hybrid signer
                    const evmSigner = {
                        address: walletClient.account.address,
                        signTypedData: async (params) => {
                            // console.log('[x402] ✍️ signTypedData CALLED with params:', {
                            //     domain: params.domain,
                            //     types: Object.keys(params.types || {}),
                            //     primaryType: params.primaryType,
                            //     message: params.message
                            // });
                            try {
                                const signature = await walletClient.signTypedData(params);
                               // console.log('[x402] ✅ signTypedData SUCCESS - signature:', signature);
                                return signature;
                            } catch (error) {
                                // console.error('[x402] ❌ signTypedData FAILED:', error);
                                throw error;
                            }
                        },
                        type: 'local',
                        source: 'json-rpc-hybrid',
                    };

                    const expectedChainId = isDevelopment ? 84532 : 8453;
                    const networkId = isDevelopment ? 'eip155:84532' : 'eip155:8453';

                    //console.log('[x402] Registering for network:', networkId, '(chainId:', expectedChainId, ')');

                    registerExactEvmScheme(paymentClient, {
                        signer: evmSigner,
                        networks: [networkId]
                    });

                    //console.log('[x402] ✅ EVM scheme registered successfully for', networkId);
                }

                // Register Solana scheme if Solana wallet is connected
                if (hasSolanaWallet) {
                    //console.log('[x402] Registering Solana scheme...');

                    const solanaSigner = {
                        publicKey: solanaWallet.publicKey,
                        address: solanaWallet.publicKey.toBase58(),
                        signTransaction: async (tx) => {
                            //console.log('[x402] 🖊️ SVM signer signTransaction CALLED');
                            const signed = await solanaWallet.signTransaction(tx);
                            //console.log('[x402] ✅ SVM signTransaction SUCCESS');
                            return signed;
                        },
                        signAllTransactions: async (txs) => {
                            //console.log('[x402] 🖊️ SVM signer signAllTransactions CALLED');
                            const signed = await solanaWallet.signAllTransactions(txs);
                            //console.log('[x402] ✅ SVM signAllTransactions SUCCESS');
                            return signed;
                        }
                    };

                    registerExactSvmScheme(paymentClient, {
                        signer: solanaSigner,
                        connection: connection
                    });

                    //console.log('[x402] ✅ Solana scheme registered successfully');
                }

                // Store the registered client in ref
                paymentClientRef.current = paymentClient;
                //console.log('[x402] ✅ Payment client registered and stored in ref');

            } catch (error) {
                // console.error('[x402] ❌ Failed to register payment client:', error);
                paymentClientRef.current = null;
            }
        };

        registerPaymentClient();
    }, [isConnected, walletClient, solanaConnected, solanaWallet, connection, isDevelopment, publicClient]);

    // Smart Base payment handler - handles connection, network switch, and payment
    const handleBasePayment = async () => {
        handleClickSound();
        playSound("snd_button_click");

        // Get expected chain ID based on environment
        const expectedChainId = isDevelopment ? 84532 : 8453; // Base Sepolia or Base Mainnet
        const currentChainId = walletClient?.chain?.id;

        // Check actual wallet connection status from MetaMask (source of truth)
        let actuallyConnected = false;
        let connectedAccounts = [];
        try {
            connectedAccounts = await window.ethereum.request({ method: 'eth_accounts' });
            actuallyConnected = connectedAccounts.length > 0;
        } catch (err) {
            console.error('[Base] Failed to check wallet connection:', err);
        }

        // console.log('[Base] Wallet state:', {
        //     isConnected,
        //     actuallyConnected,
        //     connectedAccounts,
        //     hasWalletClient: !!walletClient,
        //     hasAccount: !!walletClient?.account?.address,
        //     currentChainId,
        //     expectedChainId,
        //     currentChainName: walletClient?.chain?.name
        // });

        // Stage 1: Check if wallet is actually connected (use MetaMask as source of truth)
        if (!isConnected || !actuallyConnected) {
            //console.log('[Base] Stage 1: Wallet not connected (wagmi:', isConnected, ', actual:', actuallyConnected, '), prompting connection...');
            try {
                await connect({ connector: connectors[0] });
                return;
            } catch (error) {
                console.error('[Base] Connection failed:', error);
                setX402Error('Failed to connect wallet. Please try again.');
                return;
            }
        }

        // Stage 2: Check if on correct network (or unsupported network)
        // If walletClient is undefined, it means we're on an unsupported network
        // If currentChainId doesn't match, we're on wrong network
        if (!walletClient || currentChainId !== expectedChainId) {
            // FIRST: Check actual network from MetaMask before attempting switch
            // This prevents infinite loops when wagmi is out of sync
            let actualChainId = null;
            try {
                const currentChainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
                actualChainId = parseInt(currentChainIdHex, 16);
                //console.log('[Base] Actual wallet network:', actualChainId, 'Expected:', expectedChainId);
            } catch (err) {
                console.error('[Base] Failed to check actual network:', err);
            }

            // If wallet is already on correct network, wagmi just needs to sync
            if (actualChainId === expectedChainId) {
                //console.log('[Base] ✅ Wallet already on Base! Waiting for wagmi to sync...');
                setX402Error('⏳ Wallet syncing... Please wait a moment and click Base again.');

                // Wait briefly for wagmi to sync, then allow retry
                setTimeout(() => {
                    setX402Error('✅ Ready! Click Base to complete payment.');
                }, 2000);

                return;
            }

            // Wallet is on wrong network - need to switch
            const networkName = currentChainId
                ? `${walletClient?.chain?.name || 'Unknown'} (${currentChainId})`
                : actualChainId ? `Network ${actualChainId}` : 'unsupported network';
            //console.log(`[Base] Stage 2: Wrong network detected (${networkName}), switching to Base ${expectedChainId}...`);

            setX402Error(`Switching to Base network... Please approve in your wallet.`);

            try {
                await switchChain({ chainId: expectedChainId });
                //console.log('[Base] Network switch request sent, verifying network change...');

                // Poll to verify network actually changed (max 10 seconds)
                const maxAttempts = 20; // 20 attempts * 500ms = 10 seconds
                let attempts = 0;
                let networkChanged = false;

                while (attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Check current chain ID directly from provider (source of truth)
                    try {
                        const currentChainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
                        const currentChainIdDecimal = parseInt(currentChainIdHex, 16);

                        //console.log(`[Base] Verifying network... Attempt ${attempts + 1}/${maxAttempts}, chainId: ${currentChainIdDecimal} (expected: ${expectedChainId})`);

                        if (currentChainIdDecimal === expectedChainId) {
                            networkChanged = true;
                            //console.log('[Base] ✅ Network switch confirmed! ChainId is now', expectedChainId);
                            break;
                        }
                    } catch (err) {
                        console.error('[Base] Error checking chain ID:', err);
                    }

                    attempts++;
                }

                if (!networkChanged) {
                    console.error('[Base] ❌ Network switch timed out or was rejected');
                    setX402Error(`Network switch failed. Please manually switch to ${isDevelopment ? 'Base Sepolia' : 'Base'} network.`);
                    return;
                }

                // Network switched successfully!
                //console.log('[Base] ✅ Network switched successfully! Wallet needs to sync...');
                setX402Error('✅ Network switched! Click Base again to complete payment.');
                setBasePaymentPending(false);
                return; // User needs to click again after network switch
            } catch (error) {
                console.error('[Base] ❌ Network switch failed:', error);
                setX402Error(`Please switch to ${isDevelopment ? 'Base Sepolia' : 'Base'} network in your wallet`);
                return;
            }
        }

        // Stage 3: Wallet connected and on correct network - initiate payment!
        //console.log('[Base] Stage 3: Ready for payment, initiating x402 flow...');
        setSelectedNetwork('base');

        // Immediately trigger x402 payment (no separate button)
        if (selectedPackageIndex !== null) {
            handleX402Purchase(selectedPackageIndex);
        }
    };

    // Solana coming soon handler
    const handleSolanaComingSoon = () => {
        handleClickSound();
        playSound("snd_button_click");
        alert("Solana payments coming soon! The backend is ready for AI/bot direct calls, but Phantom wallet integration is still in development.");
    }

    // Back button handler to return to package selection
    const handleBackToPackages = () => {
        handleClickSound();
        playSound("snd_button_click");
        setShowNetworkSelection(false);
        setSelectedPackageIndex(null);
        setSelectedNetwork(null);
        setBasePaymentPending(false);
        setX402Error(null);
    }

    // Function to switch wallet network
    const handleSwitchNetwork = async () => {
        if (!wrongNetwork) return;

        try {
            //console.log('[x402] Requesting network switch to:', wrongNetwork.chainName);

            // Request network switch via MetaMask
            await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: wrongNetwork.chainIdHex }],
            });

            // Clear error and wrong network state
            setWrongNetwork(null);
            setX402Error(null);

            //console.log('[x402] Network switched successfully');
        } catch (switchError) {
            console.error('[x402] Error switching network:', switchError);

            // If network doesn't exist in wallet (error 4902), try to add it
            if (switchError.code === 4902) {
                try {
                    await window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: wrongNetwork.chainIdHex,
                            chainName: wrongNetwork.chainName,
                            rpcUrls: [wrongNetwork.rpc],
                            blockExplorerUrls: [wrongNetwork.blockExplorer],
                            nativeCurrency: {
                                name: 'Ethereum',
                                symbol: 'ETH',
                                decimals: 18
                            }
                        }],
                    });

                    setWrongNetwork(null);
                    setX402Error(null);
                    //console.log('[x402] Network added and switched successfully');
                } catch (addError) {
                    console.error('[x402] Error adding network:', addError);
                    setX402Error('Failed to add network. Please add it manually in MetaMask.');
                }
            } else {
                setX402Error('Failed to switch network. Please switch manually in MetaMask.');
            }
        }
    }

    // Unified x402 payment handler - works for both EVM and Solana
    const handleX402Purchase = async (packageIndex, retryCount = 0) => {
        // Prevent infinite retry loops
        if (retryCount > 3) {
            console.error('[x402] Too many retry attempts, stopping');
            setX402Error('Connection failed after multiple attempts. Please try again.');
            setBasePaymentPending(false);
            return;
        }

        setX402Error(null);
        setBasePaymentPending(true);
        setWaitingForNotification(false);

        try {
            // Check wallet connection and prompt if needed
            if (selectedNetwork === 'base' && (!walletClient || !isConnected)) {
                setBasePaymentPending(false);
                //console.log('[x402] EVM wallet not connected, prompting connection...');

                // Only try to connect if this is the first attempt
                if (retryCount === 0) {
                    try {
                        // Try to find the first available connector (usually injected/MetaMask)
                        const connector = connectors.find(c => c.type === 'injected') || connectors[0];
                        if (connector) {
                            await connect({ connector });
                            // Don't retry automatically - user needs to click Buy button again
                            //console.log('[x402] Wallet connected! Click Buy button again to complete payment.');
                            return;
                        } else {
                            setX402Error('No wallet detected. Please install MetaMask or Coinbase Wallet.');
                            return;
                        }
                    } catch (error) {
                        console.error('[x402] Wallet connection failed:', error);
                        if (error.message?.includes('already pending')) {
                            setX402Error('Connection request pending. Please check your wallet.');
                        } else {
                            setX402Error(error.message || 'Failed to connect wallet');
                        }
                        return;
                    }
                } else {
                    setX402Error('Please connect your EVM wallet (MetaMask, Coinbase Wallet, etc.)');
                    return;
                }
            } else if (selectedNetwork === 'solana' && !solanaWallet.connected) {
                setBasePaymentPending(false);
                //console.log('[x402] Solana wallet not connected, prompting connection...');

                // Only try to connect if this is the first attempt
                if (retryCount === 0) {
                    try {
                        await connectSolanaWallet();
                        // Don't retry automatically - user needs to click Buy button again
                        //console.log('[x402] Solana wallet connected! Click Buy button again to complete payment.');
                        return;
                    } catch (error) {
                        console.error('[x402] Solana wallet connection failed:', error);
                        setX402Error('Please connect your Solana wallet (Phantom, Solflare, etc.)');
                        return;
                    }
                } else {
                    setX402Error('Please connect your Solana wallet (Phantom, Solflare, etc.)');
                    return;
                }
            }

            // Use pre-registered payment client from ref
            const paymentClient = paymentClientRef.current;

            if (!paymentClient) {
                console.error('[x402] Payment client not registered! Wallet may still be syncing after network switch.');
                setX402Error('⏳ Wallet syncing... Click Base again in a moment.');
                setBasePaymentPending(false);
                return;
            }

            //console.log('[x402] Using pre-registered payment client from ref');

            // Wrap fetch with payment and add request/response logging
            //console.log('[x402] Creating wrapped fetch...');

            // Create logging wrapper for fetch to see actual requests
            const loggingFetch = async (url, options) => {
                // console.log('[x402] 🌐 FETCH REQUEST:', {
                //     url,
                //     method: options?.method,
                //     bodyLength: options?.body?.length || 0
                // });

                // Log headers separately to avoid truncation
                //console.log('[x402] 📤 REQUEST HEADERS:');
                if (options?.headers) {
                    Object.entries(options.headers).forEach(([key, value]) => {
                        // Truncate long values like tokens, but show payment headers fully
                        const displayValue = key.toLowerCase().includes('payment') || key.toLowerCase().includes('x-payment')
                            ? value
                            : (value.length > 50 ? value.substring(0, 50) + '...' : value);
                        //console.log(`  ${key}: ${displayValue}`);
                    });
                } else {
                    ////console.log('  (none)');
                }

                const response = await fetch(url, options);

                // Log response
                // //console.log('[x402] 📥 FETCH RESPONSE:', {
                //     status: response.status,
                //     statusText: response.statusText
                // });

                // Log response headers separately
                //console.log('[x402] 📨 RESPONSE HEADERS:');
                response.headers.forEach((value, key) => {
                    // Show payment-related headers fully, truncate others
                    const displayValue = key.toLowerCase().includes('payment')
                        ? value
                        : (value.length > 50 ? value.substring(0, 50) + '...' : value);
                    //console.log(`  ${key}: ${displayValue}`);
                });

                return response;
            };

            const fetchWithPayment = wrapFetchWithPayment(loggingFetch, paymentClient);
            //console.log('[x402] Wrapped fetch created successfully');

            // Set up notification listener BEFORE making payment request
            // (notification arrives during fetchWithPayment, not after)
            let notificationReceived = false;
            let notificationData = null;

            const handleNotification = (message) => {
                //console.log('[x402] Notification handler called:', message);
                // console.log('[x402] Checking match conditions:', {
                //     contentSource: message.content?.source,
                //     contentPackageIndex: message.content?.packageIndex,
                //     expectedPackageIndex: packageIndex,
                //     sourceMatch: message.content?.source === "x402 purchase",
                //     packageMatch: message.content?.packageIndex === packageIndex,
                //     packageIndexType: typeof message.content?.packageIndex,
                //     expectedPackageIndexType: typeof packageIndex,
                //     fullContent: message.content
                // });
                if (message.content?.source === "x402 purchase" &&
                    message.content?.packageIndex === packageIndex) {
                    //console.log('[x402] ✅ Matching notification received!');
                    notificationReceived = true;
                    notificationData = message.content;
                    eventsCenter.off(PHASER_EVENTS.PAYMENT_SUCCESS, handleNotification);
                } else {
                    console.error('[x402] ❌ Notification did not match criteria');
                }
            };

            eventsCenter.on(PHASER_EVENTS.PAYMENT_SUCCESS, handleNotification);

            // Set waiting for user to confirm in wallet
            //console.log('[x402] Requesting payment...');

            // Make payment request with comprehensive error logging
            let response;
            try {
                //console.log('[x402] Calling fetchWithPayment...');
                response = await fetchWithPayment(
                    `${API_BASE_URL}/api/purchase-chipz-x402/${packageIndex}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${session?.token}`
                        },
                        body: JSON.stringify({
                            packageIndex: packageIndex,
                            userId: session?.user_id || 'unknown'
                        })
                    }
                );
                //console.log('[x402] fetchWithPayment returned successfully');
            } catch (fetchError) {
                console.error('[x402] fetchWithPayment threw an error:', fetchError);
                console.error('[x402] Error details:', {
                    message: fetchError.message,
                    stack: fetchError.stack,
                    name: fetchError.name,
                });
                throw fetchError;
            }

            //console.log('[x402] Payment request completed with status:', response.status);

            // Check if payment was successful using x402HTTPClient
            if (response.ok) {
                //console.log('✅ [x402] Payment successful!');

                // Get payment settlement response from headers
                const httpClient = new x402HTTPClient(paymentClient);
                const paymentResponse = httpClient.getPaymentSettleResponse(
                    (name) => response.headers.get(name)
                );

                if (paymentResponse) {
                    //console.log('[x402] Payment settled:', paymentResponse);
                    //console.log('🔗 Transaction Hash:', paymentResponse.transaction);
                    if (paymentResponse.network?.includes('eip155')) {
                        //console.log('🔍 View on BaseScan:', `https://sepolia.basescan.org/tx/${paymentResponse.transaction}`);
                    }
                }

                // Get response data
                const data = await response.json();
                //console.log('[x402] Response data:', data);

                // Payment is already settled on-chain, show success immediately
                //console.log('[x402] ✅ Payment settled on-chain, showing success modal');

                // Clean up notification listener (we don't need it anymore)
                eventsCenter.off(PHASER_EVENTS.PAYMENT_SUCCESS, handleNotification);

                // Create success data with what we have
                const successData = {
                    source: "x402 purchase",
                    packageIndex: packageIndex,
                    chipzAmount: PACKAGE_CONFIG[packageIndex].chipz,
                    txHash: paymentResponse?.transaction || 'unknown',
                    message: `Successfully purchased ${PACKAGE_CONFIG[packageIndex].chipz.toLocaleString()} chipz`
                };

                // Show success modal immediately
                handlePaymentSuccess(packageIndex, successData);
            } else if (response.status === 402) {
                // Decode the payment-required header to check for specific errors
                const paymentRequiredHeader = response.headers.get('payment-required');
                if (paymentRequiredHeader) {
                    try {
                        const decoded = JSON.parse(atob(paymentRequiredHeader));
                        //console.log('[x402] Payment-required details:', decoded);

                        // Check for specific error types
                        if (decoded.error === 'insufficient_funds') {
                            const network = selectedNetwork === 'base' ? 'Base Sepolia' : 'Solana Devnet';
                            const asset = 'USDC';
                            const faucetUrl = 'https://faucet.circle.com/';

                            throw new Error(
                                `Insufficient ${asset} balance on ${network}. ` +
                                `You need ${PACKAGE_CONFIG[packageIndex].price} USDC. ` +
                                `Get testnet USDC from: ${faucetUrl}`
                            );
                        } else if (decoded.error !== 'Payment required') {
                            // Some other error besides the initial payment request
                            throw new Error(`Payment error: ${decoded.error}`);
                        }
                    } catch (parseError) {
                        if (parseError.message?.includes('Insufficient')) {
                            throw parseError; // Re-throw our insufficient funds error
                        }
                        console.warn('[x402] Could not parse payment-required header:', parseError);
                    }
                }

                // Still 402 - payment not settled yet
                //console.log('⏳ [x402] Payment in progress, waiting for settlement...');

                // Log response for debugging
                const responseBody = await response.text();
                //console.log('[x402] Response body:', responseBody);

                // Clean up notification listener since payment didn't settle
                eventsCenter.off(PHASER_EVENTS.PAYMENT_SUCCESS, handleNotification);
                setBasePaymentPending(false);
                setWaitingForNotification(false);
            } else {
                // Other error
                const errorData = await response.json().catch(() => ({}));
                eventsCenter.off(PHASER_EVENTS.PAYMENT_SUCCESS, handleNotification);
                throw new Error(errorData.message || `Request failed with status ${response.status}`);
            }

        } catch (error) {
            console.error('[x402] Payment error:', error);
            // Clean up notification listener on error
            if (typeof handleNotification !== 'undefined') {
                eventsCenter.off(PHASER_EVENTS.PAYMENT_SUCCESS, handleNotification);
            }

            // Check for MetaMask RPC error -32603 (chain mismatch)
            // This happens when wagmi has stale state and wallet is on different network
            const isChainMismatchError =
                error.code === -32603 ||
                error.error?.code === -32603 ||
                (error.message && (
                    error.message.includes('must match the active chainId') ||
                    error.message.includes('chain mismatch') ||
                    error.message.includes('wrong chain')
                ));

            if (isChainMismatchError) {
                //console.log('[x402] Detected chain mismatch error -32603, triggering network switch');

                // Extract actual chain ID from error message if possible
                const chainIdMatch = error.message?.match(/active chainId[:\s]+['"]?(\d+)/i);
                const actualChainId = chainIdMatch ? parseInt(chainIdMatch[1]) : null;

                //console.log('[x402] Wallet appears to be on chain:', actualChainId || 'unknown');
                //console.log('[x402] Expected Base mainnet (8453)');

                // Clear pending state
                setBasePaymentPending(false);
                setWaitingForNotification(false);

                // Show message and trigger network switch
                setX402Error('🔄 Network mismatch detected! Switching to Base...');

                // Trigger network switch
                setTimeout(async () => {
                    try {
                        const expectedChainId = isDevelopment ? 84532 : 8453;
                        await switchChain({ chainId: expectedChainId });

                        // Poll to verify network actually changed
                        let networkChanged = false;
                        for (let attempts = 0; attempts < 20; attempts++) {
                            await new Promise(resolve => setTimeout(resolve, 500));

                            const currentChainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
                            const currentChainIdDecimal = parseInt(currentChainIdHex, 16);

                            if (currentChainIdDecimal === expectedChainId) {
                                networkChanged = true;
                                break;
                            }
                        }

                        if (networkChanged) {
                            setX402Error('✅ Network switched to Base! Click Base again to complete payment.');
                        } else {
                            setX402Error('⚠️ Network switch timeout. Please manually switch to Base and try again.');
                        }
                    } catch (switchError) {
                        console.error('[x402] Auto network switch failed:', switchError);
                        setX402Error('⚠️ Please manually switch to Base network and try again.');
                    }
                }, 500);

                return; // Exit error handler
            }

            // Handle specific x402 errors (from documentation)
            if (error.message?.includes("No scheme registered")) {
                setX402Error('Network not supported - please connect the correct wallet');
            } else if (error.message?.includes("Payment already attempted")) {
                setX402Error('Payment failed on retry - please try again');
            } else if (error.message?.includes("missing")) {
                setX402Error('Configuration error - please refresh and try again');
            } else {
                setX402Error(error.message || 'Payment failed');
            }

            setBasePaymentPending(false);
            setWaitingForNotification(false);
        }
    };

    // Handle successful payment after receiving notification
    const handlePaymentSuccess = async (itemIndex, notificationContent) => {
        try {
            // Track conversion
            await trackChipzConversion(itemIndex);

            // Check if we have the required props for full success flow (from lobby.jsx)
            const hasFullSuccessFlow =
                props.setZoomBalance && typeof props.setZoomBalance === 'function' &&
                props.setShowSuccessModal && typeof props.setShowSuccessModal === 'function' &&
                props.setSuccessPackage && typeof props.setSuccessPackage === 'function';

            if (hasFullSuccessFlow) {
                // Full success flow: refresh balance, show success modal (from lobby.jsx)

                // Refresh wallet balance
                try {
                    const account = await client.getAccount(session);
                    const newBalance = parseInt(JSON.parse(account.wallet).zoom) || 0;
                    props.setZoomBalance(newBalance);
                } catch (error) {
                    console.error('[x402] Failed to refresh chipz balance:', error);
                }

                // Close shop modal
                setX402Loading(false);
                setBasePaymentPending(false);
                setWaitingForNotification(false);
                props.setShowShopModal(false);
                setPayType(0);

                // Show success modal
                const successPackageIndex = itemIndex === 0 ? 1 : itemIndex + 1;
                props.setSuccessPackage(successPackageIndex);
                props.setShowSuccessModal(true);
            } else {
                // Basic success flow: just close modal and show alert (from other components)

                // Still try to refresh balance even in basic flow (if we have client access)
                try {
                    const account = await client.getAccount(session);
                    const newBalance = parseInt(JSON.parse(account.wallet).zoom) || 0;

                    // Try to update via props if available
                    if (props.setZoomBalance && typeof props.setZoomBalance === 'function') {
                        props.setZoomBalance(newBalance);
                    }
                } catch (error) {
                    console.error('[x402] Failed to refresh chipz balance:', error);
                }

                setX402Loading(false);
                setBasePaymentPending(false);
                setWaitingForNotification(false);
                props.setShowShopModal(false);
                setPayType(0);

                // Show simple success message
                const chipzAmount = notificationContent?.chipzAmount || 'chipz';
                alert(`Successfully purchased ${chipzAmount.toLocaleString()}! Your balance has been updated.`);
            }
        } catch (error) {
            console.error('[x402] Error in payment success handler:', error);
        }
    }

    async function trackChipzConversion(index) {
        const conversion = gtagConversionMap[index];
        if (conversion) {
            try {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'chipz_purchase',
                    'conversionValue': conversion.value,
                    'currency': 'USD'
                });
            } catch (error) {
                console.error("Error tracking chipz conversion:", error);
            }
        }
    }

    // Compute instruction message for user
    const getPaymentInstruction = () => {
        const expectedChainId = isDevelopment ? 84532 : 8453;
        const currentChainId = walletClient?.chain?.id;

        // Show x402Error if present (includes network switch success message)
        if (x402Error) {
            const isSuccess = x402Error.includes('✅');
            const isLoading = x402Error.includes('⏳') || x402Error.includes('🔄');
            return {
                text: x402Error,
                color: isSuccess ? '#00FF00' : '#FF6B6B',
                showSpinner: isLoading
            };
        }

        if (basePaymentPending) {
            return {
                text: 'Processing... Approve transaction in your wallet',
                color: '#FFFF00',
                showSpinner: true
            };
        }

        if (!isConnected) {
            return {
                text: '👛 Click Base to connect your wallet',
                color: '#FFD700',
                showSpinner: false
            };
        }

        if (!walletClient?.account?.address || currentChainId !== expectedChainId) {
            const networkName = isDevelopment ? 'Base Sepolia' : 'Base';
            return {
                text: `🔄 Click Base to switch to ${networkName}`,
                color: '#FF6B6B',
                showSpinner: false
            };
        }

        // Ready for payment
        return {
            text: `✅ Click Base to purchase with USDC ($${formattedUsdcBalance.toFixed(2)} available)`,
            color: formattedUsdcBalance > 0 ? '#00FF00' : '#FF6B6B',
            showSpinner: false
        };
    };

    const instruction = getPaymentInstruction();

    return (
        <Modal className="stripe-cart" show={props.showShopModal} onShow={() => { }} onHide={() => { props.setShowShopModal(false); setPayType(0); setShowNetworkSelection(false); setSelectedPackageIndex(null); setSelectedNetwork(null); setBasePaymentPending(false); setX402Error(null); }} centered fullscreen="true">
            <div className="whole-container">
                {payType === 0 ? (
                    <>
                        <Row className="disclaimer">
                            <Col>
                                <h1 className="pay-type-header text-center">Choose Payment Method</h1>
                            </Col>
                        </Row>
                        <Row className="disclaimer mt-5 justify-content-center align-items-center">
                            <Col xs={12} md={6} onClick={() => setPayType(1)}>
                                <div className="pay-type-img-btn">
                                    <img src={creditCardIcon} className="pay-type-image" alt="Credit Card" /><br />
                                    <h1>Pay with Credit Card</h1>
                                </div>
                            </Col>
                            <Col xs={12} md={6} onClick={() => setPayType(2)} className="justify-content-center">
                                <div className="pay-type-img-btn text-center">
                                    <img src={usdcIcon} className="pay-type-image" alt="USDC" /><br />
                                    <h1>Pay with Crypto</h1>
                                </div>
                            </Col>
                        </Row>
                    </>
                ) : payType === 1 ? (
                    <div
                        dangerouslySetInnerHTML={{
                            __html:
                                `
                                <stripe-pricing-table 
                                    pricing-table-id="prctbl_1OJznALPlwL0aA7e42bpXTv4"
                                    publishable-key="pk_live_51NZdCkLPlwL0aA7eDuhYSktBPfuOXpgjJ4cWqnHHpm5dYgAAByHMZrLLzKBtHZHQMuwfspBoAebrkcWgKa8TrYkD00iNZFgKCq"
                                    client-reference-id="${session.user_id}"
                                >
                                </stripe-pricing-table>
                            `
                        }}
                    />
                ) : payType === 2 ? (
                    showNetworkSelection ? (
                        <>
                            <Row className="disclaimer">
                                <Col>
                                    <h1 className="pay-type-header text-center">Choose Network</h1>
                                </Col>
                            </Row>
                            {selectedPackageIndex !== null && urlData[selectedPackageIndex] && (
                                <Row className="disclaimer mt-3">
                                    <Col xs={6} className="text-left">
                                        <span className="pay-type-header">{urlData[selectedPackageIndex].name}</span>
                                    </Col>
                                    <Col xs={6} className="text-right">
                                        <span className="pay-type-amount">${urlData[selectedPackageIndex].amount}</span>
                                    </Col>
                                </Row>
                            )}
                            <Row className="disclaimer mt-4">
                                <Col xs={12} className="text-center mb-3">
                                    <Button
                                        variant="secondary"
                                        size="lg"
                                        onClick={handleBackToPackages}
                                        style={{ fontSize: "2rem", padding: "8px 20px" }}
                                    >
                                        ← Back to Packages
                                    </Button>
                                </Col>
                            </Row>
                            {basePaymentPending && (
                                <Row className="disclaimer mt-3 mb-3">
                                    <Col className="text-center">
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "15px" }}>
                                            {!waitingForNotification ? (
                                                <>
                                                    {/* Waiting for MetaMask confirmation */}
                                                    <p className="container-body-text" style={{ fontSize: "2.6rem", color: "#FFFF00", marginBottom: "0" }}>
                                                        ⚠️ Pending confirmation in your wallet..
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    {/* After confirmation, waiting for notification */}
                                                    <Spinner animation="grow" variant="info" style={{ width: "3rem", height: "3rem" }} />
                                                    <p className="container-body-text" style={{ fontSize: "2.6rem", color: "#00D1FF", marginBottom: "0" }}>
                                                        💳✨ Processing purchase... 🚀
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            )}
                            {x402Error && (
                                <Row className="disclaimer mt-3 mb-3">
                                    <Col className="text-center">
                                        <p className="container-body-text" style={{ fontSize: "2.6rem", color: "#FF6B6B" }}>
                                            {x402Error}
                                        </p>
                                        {wrongNetwork && (
                                            <div style={{ marginTop: "20px" }}>
                                                <Button
                                                    variant="primary"
                                                    size="lg"
                                                    onClick={handleSwitchNetwork}
                                                    style={{
                                                        fontSize: "2rem",
                                                        padding: "12px 30px",
                                                        backgroundColor: "#00D1FF",
                                                        borderColor: "#00D1FF",
                                                        color: "#000"
                                                    }}
                                                >
                                                    🔄 Switch to {wrongNetwork.chainName}
                                                </Button>
                                            </div>
                                        )}
                                        {(x402Error.includes("connect") || x402Error.includes("authorize") || x402Error.includes("connection lost")) && !isConnected && (
                                            <div style={{ marginTop: "20px", width: "20%", marginLeft: "auto", marginRight: "auto" }}>
                                                <WalletConnect />
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            )}
                            {/* Instruction label - tells user what to do */}
                            <Row className="mt-3 mb-2 justify-content-center">
                                <Col xs={12} md={8} lg={6} className="text-center">
                                    <div style={{ fontSize: "1.1rem", color: instruction.color, fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                        {instruction.showSpinner && <Spinner animation="grow" variant="primary" size="sm" />}
                                        {instruction.text}
                                    </div>
                                </Col>
                            </Row>
                            {/* Network selection icons */}
                            <Row className="disclaimer mt-2 justify-content-center align-items-center">
                                <Col xs={12} md={6} lg={3} onClick={() => !basePaymentPending && handleBasePayment()} className="justify-content-center" style={{ opacity: basePaymentPending ? 0.5 : 1, cursor: basePaymentPending ? 'not-allowed' : 'pointer' }}>
                                    <div className="pay-type-img-btn text-center">
                                        <img src={baseIcon} className="pay-type-image" alt="Base" /><br />
                                        {/* Simple label under icon */}
                                        <p style={{ fontSize: "0.9rem", color: "#FFFFFF", marginTop: "10px" }}>
                                            {isConnected && walletClient?.chain?.id === (isDevelopment ? 84532 : 8453)
                                                ? `$${formattedUsdcBalance.toFixed(2)} USDC`
                                                : 'Base'}
                                        </p>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={3} onClick={() => !basePaymentPending && handleSolanaComingSoon()} className="justify-content-center" style={{ opacity: basePaymentPending ? 0.5 : 1, cursor: basePaymentPending ? 'not-allowed' : 'pointer' }}>
                                    <div className="pay-type-img-btn text-center">
                                        <img src={solanaIcon} className="pay-type-image" alt="Solana" /><br />
                                        <p style={{ fontSize: "0.8rem", color: "#FFD700", marginTop: "10px" }}>Coming Soon</p>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <Row className="pay-type-all-text">
                                <Col>
                                    <img src={chipz1} className="pay-type-image" alt="100,000 Chipz" /><br />
                                    <span className="text-left pay-type-header">100,000 Chipz</span><br />
                                    <p className="text-left pay-type-desc">Receive 100,000 Chipz</p><br />
                                    <p className="text-left pay-type-amount">$0.99</p>
                                    <Button
                                        className="pay-type-btn"
                                        variant="success"
                                        size="lg"
                                        onClick={() => {
                                            handleClickSound();
                                            playSound("snd_button_click");
                                            setSelectedPackageIndex(0);
                                            setShowNetworkSelection(true);
                                        }}
                                    >
                                        <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                        &nbsp; Buy
                                    </Button>
                                </Col>
                                <Col>
                                    <img src={chipz2} className="pay-type-image" alt="525,000 Chipz" /><br />
                                    <span className="text-left pay-type-header">525,000 Chipz</span><br />
                                    <p className="text-left pay-type-desc">Receive 500,000 Chipz + a 5% bonus of 25,000 Chipz</p><br />
                                    <p className="text-left pay-type-amount">$4.99</p>
                                    <Button
                                        className="pay-type-btn"
                                        variant="success"
                                        size="lg"
                                        onClick={() => {
                                            handleClickSound();
                                            playSound("snd_button_click");
                                            setSelectedPackageIndex(1);
                                            setShowNetworkSelection(true);
                                        }}
                                    >
                                        <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                        &nbsp; Buy
                                    </Button>
                                </Col>
                                <Col>
                                    <img src={chipz3} className="pay-type-image" alt="525,000 Chipz" /><br />
                                    <span className="text-left pay-type-header">1,070,000 Chipz</span><br />
                                    <p className="text-left pay-type-desc">Receive 1,000,000 Chipz + a 7% bonus of 70,000 Chipz</p><br />
                                    <p className="text-left pay-type-amount">$9.99</p>
                                    <Button
                                        className="pay-type-btn"
                                        variant="success"
                                        size="lg"
                                        onClick={() => {
                                            handleClickSound();
                                            playSound("snd_button_click");
                                            setSelectedPackageIndex(2);
                                            setShowNetworkSelection(true);
                                        }}
                                    >
                                        <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                        &nbsp; Buy
                                    </Button>
                                </Col>
                                <Col>
                                    <img src={chipz4} className="pay-type-image" alt="525,000 Chipz" /><br />
                                    <span className="text-left pay-type-header">5,500,000 Chipz</span><br />
                                    <p className="text-left pay-type-desc">Receive 5,000,000 Chipz + a 10% bonus of 500,000 Chipz</p><br />
                                    <p className="text-left pay-type-amount">$49.99</p>
                                    <Button
                                        className="pay-type-btn"
                                        variant="success"
                                        size="lg"
                                        onClick={() => {
                                            handleClickSound();
                                            playSound("snd_button_click");
                                            setSelectedPackageIndex(3);
                                            setShowNetworkSelection(true);
                                        }}
                                    >
                                        <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                        &nbsp; Buy
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )
                ) : (
                    <>
                        {/* x402 Payment Option */}
                        <Row className="disclaimer mb-4">
                            <Col className="text-center">
                                <p className="container-body-text" style={{ fontSize: "1.2rem", color: "#FFFF00", marginBottom: "20px" }}>
                                    {!isConnected ? "Connect your wallet to pay with x402" : ""}
                                </p>
                                <WalletConnect />
                            </Col>
                        </Row>
                        {x402Error && (
                            <Row className="disclaimer mb-4">
                                <Col className="text-center">
                                    <p className="container-body-text" style={{ fontSize: "1rem", color: "#FF6B6B" }}>
                                        {x402Error}
                                    </p>
                                </Col>
                            </Row>
                        )}
                        <Row className="pay-type-all-text">
                            <Col>
                                <img src={chipz1} className="pay-type-image" alt="100,000 Chipz" /><br />
                                <span className="text-left pay-type-header">100,000 Chipz</span><br />
                                <p className="text-left pay-type-desc">Receive 100,000 Chipz</p><br />
                                <p className="text-left pay-type-amount">$0.99</p>
                                <Button
                                    className="pay-type-btn"
                                    variant="success"
                                    size="lg"
                                    onClick={() => {
                                        handleClickSound();
                                        playSound("snd_button_click");
                                        handleX402Payment(0);
                                    }}
                                    disabled={x402Loading || !isConnected}
                                >
                                    {x402Loading ? "Processing..." : (
                                        <>
                                            <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                            &nbsp; Buy with x402
                                        </>
                                    )}
                                </Button>
                            </Col>
                            <Col>
                                <img src={chipz2} className="pay-type-image" alt="525,000 Chipz" /><br />
                                <span className="text-left pay-type-header">525,000 Chipz</span><br />
                                <p className="text-left pay-type-desc">Receive 500,000 Chipz + a 5% bonus of 25,000 Chipz</p><br />
                                <p className="text-left pay-type-amount">$4.99</p>
                                <Button
                                    className="pay-type-btn"
                                    variant="success"
                                    size="lg"
                                    onClick={() => {
                                        handleClickSound();
                                        playSound("snd_button_click");
                                        handleX402Payment(1);
                                    }}
                                    disabled={x402Loading || !isConnected}
                                >
                                    {x402Loading ? "Processing..." : (
                                        <>
                                            <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                            &nbsp; Buy with x402
                                        </>
                                    )}
                                </Button>
                            </Col>
                            <Col>
                                <img src={chipz3} className="pay-type-image" alt="1,070,000 Chipz" /><br />
                                <span className="text-left pay-type-header">1,070,000 Chipz</span><br />
                                <p className="text-left pay-type-desc">Receive 1,000,000 Chipz + a 7% bonus of 70,000 Chipz</p><br />
                                <p className="text-left pay-type-amount">$9.99</p>
                                <Button
                                    className="pay-type-btn"
                                    variant="success"
                                    size="lg"
                                    onClick={() => {
                                        handleClickSound();
                                        playSound("snd_button_click");
                                        handleX402Payment(2);
                                    }}
                                    disabled={x402Loading || !isConnected}
                                >
                                    {x402Loading ? "Processing..." : (
                                        <>
                                            <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                            &nbsp; Buy with x402
                                        </>
                                    )}
                                </Button>
                            </Col>
                            <Col>
                                <img src={chipz4} className="pay-type-image" alt="5,500,000 Chipz" /><br />
                                <span className="text-left pay-type-header">5,500,000 Chipz</span><br />
                                <p className="text-left pay-type-desc">Receive 5,000,000 Chipz + a 10% bonus of 500,000 Chipz</p><br />
                                <p className="text-left pay-type-amount">$49.99</p>
                                <Button
                                    className="pay-type-btn"
                                    variant="success"
                                    size="lg"
                                    onClick={() => {
                                        handleClickSound();
                                        playSound("snd_button_click");
                                        handleX402Payment(3);
                                    }}
                                    disabled={x402Loading || !isConnected}
                                >
                                    {x402Loading ? "Processing..." : (
                                        <>
                                            <img src={usdcIcon} style={{ maxWidth: '28px' }} alt="" />
                                            &nbsp; Buy with x402
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        </Modal>
    )
}